import { motion, useInView } from "framer-motion";
import { useRef } from "react";
import { Users, Heart, Award, Leaf } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import WordFadeIn from "@/components/WordFadeIn";
import DrawIcon from "@/components/DrawIcon";
import Seo from "@/components/Seo";

const values = [
  { icon: Heart, title: "Familie", desc: "Peter Kotlarski, seine Frau und seine Tochter arbeiten gemeinsam im Markt mit." },
  { icon: Users, title: "Team", desc: "Ein 26-köpfiges Team mit starkem Zusammenhalt und persönlichem Miteinander." },
  { icon: Award, title: "Erfahrung", desc: "Peter Kotlarski ist seit 1982 bei REWE und seit 2001 selbstständiger Inhaber." },
  { icon: Leaf, title: "Nähe", desc: "Familiäre Atmosphäre, in der jeder Kunde willkommen ist." },
];

const stats = [
  { value: "seit 1982", label: "bei REWE" },
  { value: "seit 2001", label: "selbstständig" },
  { value: "26", label: "Mitarbeitende" },
  { value: "40+", label: "Jahre Handelserfahrung" },
];

const UeberUns = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-80px" });

  return (
    <>
      <Seo title="Über uns | Rewe Kotlarski Meerbusch-Osterath" description="Peter Kotlarski, Familie und ein Team aus 26 Mitarbeitenden führen den Markt in Meerbusch-Osterath mit persönlichem Miteinander und viel Erfahrung." />
      <PageHeader
        tag="Über uns"
        title="Familie, Team und Handelserfahrung"
        subtitle="Lernen Sie Peter Kotlarski, die Familiengeschichte und das 26-köpfige Team von Rewe Kotlarski in Meerbusch-Osterath kennen."
        heroImage="/images/regal-team.webp"
        objectPosition="center 30%"
        drawIconVariant="heart"
      />

      {/* Values */}
      <section ref={ref} className="relative py-20 px-6 md:px-16 bg-secondary/30 overflow-hidden">
        <DrawIcon
          variant="heart"
          size={160}
          delay={0.5}
          className="absolute right-8 md:right-16 top-8 opacity-15 hidden md:block"
        />
        <div className="max-w-6xl mx-auto relative z-10">
          <WordFadeIn
            text="Familie und Haltung"
            as="h2"
            className="font-display text-3xl md:text-5xl font-bold text-foreground text-center md:text-left mb-16 [hyphens:none]"
          />

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((v, i) => (
              <motion.div
                key={v.title}
                initial={{ opacity: 0, y: 40 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: 0.1 * i }}
                className="bg-card border border-border p-8 flex flex-col items-center text-center md:items-start md:text-left h-full"
              >
                <v.icon className="w-8 h-8 text-primary-bright mb-5 shrink-0" aria-hidden="true" />
                <h3 className="font-display text-xl font-semibold text-foreground mb-2" style={{ textWrap: "pretty" as any }}>{v.title}</h3>
                <p className="text-muted-foreground font-body leading-relaxed" style={{ textWrap: "pretty" as any }}>{v.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Story */}
      <section className="py-20 px-6 md:px-16">
        <div className="max-w-5xl mx-auto text-center md:text-left space-y-6">
          <WordFadeIn
            text="Inhabergeschichte"
            as="h2"
            className="font-display text-3xl md:text-5xl font-bold text-foreground [hyphens:none]"
          />
          <p className="text-muted-foreground font-body text-lg leading-relaxed max-w-3xl mx-auto md:mx-0" style={{ textWrap: "pretty" as any }}>
            Peter Kotlarski ist seit 1982 bei REWE tätig und verfügt über mehr als 40 Jahre Erfahrung im Handel. Bevor er seinen eigenen Markt führte, war er als Marktleiter in mehreren Märkten tätig. Seit 2001 ist er selbstständiger Inhaber eines eigenen Marktes.
          </p>
          <p className="text-muted-foreground font-body text-lg leading-relaxed max-w-3xl mx-auto md:mx-0" style={{ textWrap: "pretty" as any }}>
            Diese langjährige Erfahrung bildet die Grundlage für Rewe Kotlarski und verbindet fundierte Handelskenntnis mit einer familiären Unternehmensstruktur, in der auch seine Frau und seine Tochter mitwirken. Zum Markt gehört ein 26-köpfiges Team, das den Zusammenhalt und das persönliche Miteinander täglich mitträgt.
          </p>
        </div>
      </section>

      {/* Stats */}
      <section className="py-20 px-6 md:px-16 bg-secondary/30">
        <div className="max-w-6xl mx-auto grid grid-cols-2 lg:grid-cols-4 gap-8">
          {stats.map((s, i) => (
            <motion.div
              key={s.label}
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 * i }}
              className="text-center"
            >
              <p className="font-display text-4xl md:text-5xl font-bold text-primary-bright">{s.value}</p>
              <p className="text-muted-foreground font-body text-sm mt-2" style={{ textWrap: "pretty" as any }}>{s.label}</p>
            </motion.div>
          ))}
        </div>
      </section>
    </>
  );
};

export default UeberUns;
